// PayerTariffsPackagesTab.tsx — Admin Panel -> Insurance Claims -> Payer
// tariffs & packages. Insurance Claims Phase 6.1
// (docs/HMS_V4_INSURANCE_CLAIMS_DESIGN.md §4.4), wireframe S9's 3rd tab —
// the flag (`claims.payer_tariffs`), hospital/payer tariffs, and the
// package catalogue (+ CSV import for package code lists, the only one of
// the four Insurance Claims admin tabs this design calls for it on).
//
// The tab always renders — there would be no way to ever turn the flag on
// otherwise — but everything below the flag card is read-only context
// (not hidden) while it's off, since existing packages/tariffs may already
// be configured ahead of switching it on.
import { useState, useEffect, useCallback } from 'react';
import {
  getPayerTariffsSettings, updatePayerTariffsSettings,
  getPayerTariffs, createPayerTariff, deletePayerTariff,
  getPackages, createPackage, updatePackage, deletePackage, bulkImportPackages, downloadPackageCsvTemplate,
  getPayers, getServiceMaster,
} from '../api';
import { CLAIM_HEADS, type PayerTariffsSettings, type PayerTariff, type Package, type Payer, type ServiceMasterItem } from '../types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { LoadingState } from '@/components/ui/States';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { TabBar } from '@/components/ui/TabBar';

// payer_tariffs.effective_from/_to are DATE columns; pg's default type
// parser returns them as a JS Date, which Express's res.json() then
// stringifies to a full ISO timestamp ("2026-10-14T00:00:00.000Z") rather
// than the plain "2026-10-14" the form itself writes — slicing is safe for
// both shapes.
function shortDate(v: string | null | undefined): string {
  return v ? String(v).slice(0, 10) : '';
}

function HeadsPicker({ label, value, onChange }: { label: string; value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (h: string) => onChange(value.includes(h) ? value.filter((x) => x !== h) : [...value, h]);
  return (
    <FormField label={label} className="col-span-3">
      <div className="flex flex-wrap gap-2">
        {CLAIM_HEADS.map((h) => (
          <label key={h} className="flex items-center gap-1 rounded-[var(--radius)] border border-border px-2 py-1 text-xs">
            <Checkbox checked={value.includes(h)} onCheckedChange={() => toggle(h)} />
            {h}
          </label>
        ))}
      </div>
    </FormField>
  );
}

function PackagesPanel() {
  const [items, setItems] = useState<Package[] | null>(null);
  const [payers, setPayers] = useState<Payer[]>([]);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importResult, setImportResult] = useState<{ created: number; updated: number; failed: number } | null>(null);
  const [importing, setImporting] = useState(false);

  const BLANK = {
    code: '', name: '', specialty: '', scheme_package_code: '', payer_id: '', room_category: '',
    los_days: '', rate: '0', includes_heads: [] as string[], excludes_heads: [] as string[],
    icu_days_included: '', implant_included: false,
  };
  const [draft, setDraft] = useState(BLANK);

  const load = useCallback(() => {
    getPackages({ active: 'all' }).then(setItems).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(() => { load(); getPayers({ active: 'all' }).then(setPayers).catch(() => {}); }, [load]);

  const submit = async () => {
    if (!draft.code.trim() || !draft.name.trim()) return;
    setSaving(true); setError('');
    try {
      await createPackage({
        code: draft.code.trim(), name: draft.name.trim(), rate: Number(draft.rate) || 0,
        specialty: draft.specialty.trim() || undefined, scheme_package_code: draft.scheme_package_code.trim() || undefined,
        payer_id: draft.payer_id ? Number(draft.payer_id) : null,
        room_category: draft.room_category.trim() || undefined, los_days: draft.los_days ? Number(draft.los_days) : undefined,
        includes_heads: draft.includes_heads as Package['includes_heads'], excludes_heads: draft.excludes_heads as Package['excludes_heads'],
        icu_days_included: draft.icu_days_included ? Number(draft.icu_days_included) : undefined,
        implant_included: draft.implant_included,
      });
      setDraft(BLANK); setAdding(false); await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setSaving(false);
  };

  const toggleActive = async (p: Package) => {
    setError('');
    try { await updatePackage(p.id, { active: !p.active }); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const remove = async (p: Package) => {
    setError('');
    try { await deletePackage(p.id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const onImportFile = async (file: File | null) => {
    if (!file) return;
    setImporting(true); setError(''); setImportResult(null);
    try {
      const result = await bulkImportPackages(file);
      setImportResult({ created: result.created, updated: result.updated, failed: result.failed });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setImporting(false);
  };

  if (!items) return <LoadingState label="Loading packages…" />;

  const columns: DataTableColumn<Package>[] = [
    { key: 'code', header: 'Code', render: (p) => <span className="font-medium">{p.code}</span> },
    {
      key: 'name', header: 'Name', sortable: true, sortValue: (p) => p.name,
      render: (p) => <>{p.name}{!p.active && <Badge variant="neutral" className="ml-2">Inactive</Badge>}</>,
    },
    { key: 'payer', header: 'Payer', render: (p) => p.payer_name || <span className="text-ink-muted">Hospital</span> },
    { key: 'rate', header: 'Rate', align: 'right', render: (p) => `₹${Number(p.rate).toFixed(2)}` },
    { key: 'los', header: 'LOS days', align: 'right', render: (p) => p.los_days ?? '—' },
    { key: 'includes', header: 'Includes', render: (p) => p.includes_heads?.join(', ') || '—' },
    { key: 'excludes', header: 'Excludes', render: (p) => p.excludes_heads?.join(', ') || '—' },
    {
      key: 'actions', header: '', align: 'right',
      render: (p) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => toggleActive(p)}>{p.active ? 'Deactivate' : 'Reactivate'}</Button>
          <Button size="sm" variant="danger" onClick={() => remove(p)}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="fade-in">
      <div className="mb-3 text-xs text-ink-muted">
        A package bundles a set of claim heads (room, nursing, OT, …) into one lump-sum rate for a scheme
        (PM-JAY/CMCHIS) or hospital package. Applying a package to an admission zero-rates every new charge whose
        head is in &quot;Includes&quot; — the breakup bill shows it as &quot;Included in package&quot;.
        Heads in &quot;Excludes&quot;, or named in neither list, are billed normally.
      </div>

      {error && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}
      {importResult && (
        <div className="mb-3 rounded-[var(--radius)] bg-success-soft px-3 py-2 text-xs text-success-ink">
          Import complete — {importResult.created} created, {importResult.updated} updated, {importResult.failed} failed.
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-xs text-link underline"
            onClick={() => downloadPackageCsvTemplate().catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))}
          >
            Download CSV template
          </button>
          <label className="inline-flex cursor-pointer items-center">
            <Button size="sm" variant="ghost" loading={importing} disabled={importing} onClick={(e) => { e.preventDefault(); (e.currentTarget.nextSibling as HTMLInputElement)?.click(); }}>
              📥 Import CSV
            </Button>
            <input type="file" accept=".csv" className="hidden" onChange={(e) => onImportFile(e.target.files?.[0] ?? null)} />
          </label>
        </div>
        {!adding && <Button size="sm" onClick={() => setAdding(true)}>+ Add package</Button>}
      </div>

      {adding && (
        <Card className="mb-4 p-4">
          <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3">
            <FormField label="Code" required>
              <Input value={draft.code} onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))} placeholder="e.g. PMJAY-LAPCHOLE" />
            </FormField>
            <FormField label="Name" required className="col-span-2">
              <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
            </FormField>
            <FormField label="Payer (blank = hospital package)">
              <NativeSelect value={draft.payer_id} onChange={(e) => setDraft((d) => ({ ...d, payer_id: e.target.value }))}>
                <option value="">Hospital</option>
                {payers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Scheme package code">
              <Input value={draft.scheme_package_code} onChange={(e) => setDraft((d) => ({ ...d, scheme_package_code: e.target.value }))} />
            </FormField>
            <FormField label="Specialty">
              <Input value={draft.specialty} onChange={(e) => setDraft((d) => ({ ...d, specialty: e.target.value }))} />
            </FormField>
            <FormField label="Room category">
              <Input value={draft.room_category} onChange={(e) => setDraft((d) => ({ ...d, room_category: e.target.value }))} />
            </FormField>
            <FormField label="Rate ₹" required>
              <Input type="number" min={0} value={draft.rate} onChange={(e) => setDraft((d) => ({ ...d, rate: e.target.value }))} />
            </FormField>
            <FormField label="LOS days">
              <Input type="number" min={0} value={draft.los_days} onChange={(e) => setDraft((d) => ({ ...d, los_days: e.target.value }))} />
            </FormField>
            <FormField label="ICU days included">
              <Input type="number" min={0} value={draft.icu_days_included} onChange={(e) => setDraft((d) => ({ ...d, icu_days_included: e.target.value }))} />
            </FormField>
            <HeadsPicker label="Includes (zero-rated when covered)" value={draft.includes_heads} onChange={(v) => setDraft((d) => ({ ...d, includes_heads: v }))} />
            <HeadsPicker label="Excludes (billed normally)" value={draft.excludes_heads} onChange={(v) => setDraft((d) => ({ ...d, excludes_heads: v }))} />
          </div>
          <label className="mb-3 flex items-center gap-1.5 text-xs">
            <Checkbox checked={draft.implant_included} onCheckedChange={(v) => setDraft((d) => ({ ...d, implant_included: !!v }))} />
            Implant included in package rate
          </label>
          <div className="flex gap-2">
            <Button size="sm" disabled={saving || !draft.code.trim() || !draft.name.trim()} loading={saving} onClick={submit}>Save package</Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setDraft(BLANK); }}>Cancel</Button>
          </div>
        </Card>
      )}

      <DataTable columns={columns} rows={items} rowKey={(p) => p.id} rowClassName={(p) => (p.active ? undefined : 'opacity-50')}
        emptyLabel="No packages yet" emptyHint="Add one above, or import a CSV code list." />
    </div>
  );
}

function PayerTariffsPanel() {
  const [items, setItems] = useState<PayerTariff[] | null>(null);
  const [payers, setPayers] = useState<Payer[]>([]);
  const [services, setServices] = useState<ServiceMasterItem[]>([]);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const BLANK = { payer_id: '', service_id: '', room_category: '', rate: '0', effective_from: '', effective_to: '' };
  const [draft, setDraft] = useState(BLANK);

  const load = useCallback(() => {
    getPayerTariffs().then(setItems).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(() => {
    load();
    getPayers({ active: 'all' }).then(setPayers).catch(() => {});
    getServiceMaster({ active: 'all' }).then(setServices).catch(() => {});
  }, [load]);

  const submit = async () => {
    if (!draft.service_id || !draft.rate) return;
    setSaving(true); setError('');
    try {
      await createPayerTariff({
        payer_id: draft.payer_id ? Number(draft.payer_id) : null,
        service_id: Number(draft.service_id), rate: Number(draft.rate) || 0,
        room_category: draft.room_category.trim() || undefined,
        effective_from: draft.effective_from || undefined, effective_to: draft.effective_to || undefined,
      });
      setDraft(BLANK); setAdding(false); await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setSaving(false);
  };

  const remove = async (t: PayerTariff) => {
    setError('');
    try { await deletePayerTariff(t.id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  if (!items) return <LoadingState label="Loading payer tariffs…" />;

  const columns: DataTableColumn<PayerTariff>[] = [
    { key: 'service', header: 'Service', render: (t) => <>{t.service_name} <span className="text-ink-muted">({t.service_code})</span></> },
    { key: 'payer', header: 'Payer', render: (t) => t.payer_name || <span className="text-ink-muted">Hospital</span> },
    { key: 'room', header: 'Room category', render: (t) => t.room_category || '—' },
    { key: 'rate', header: 'Rate', align: 'right', render: (t) => `₹${Number(t.rate).toFixed(2)}` },
    { key: 'from', header: 'From', render: (t) => shortDate(t.effective_from) },
    { key: 'to', header: 'To', render: (t) => shortDate(t.effective_to) || '—' },
    { key: 'actions', header: '', align: 'right', render: (t) => <Button size="sm" variant="danger" onClick={() => remove(t)}>Delete</Button> },
  ];

  return (
    <div className="fade-in">
      <div className="mb-3 text-xs text-ink-muted">
        A hospital-rate row (payer blank) is the fallback every service uses once the flag above is on — before a
        payer-specific negotiated rate is added for the same service.
      </div>
      {error && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}
      <div className="mb-3 flex justify-end">
        {!adding && <Button size="sm" onClick={() => setAdding(true)}>+ Add tariff</Button>}
      </div>
      {adding && (
        <Card className="mb-4 p-4">
          <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3">
            <FormField label="Service" required className="col-span-2">
              <NativeSelect value={draft.service_id} onChange={(e) => setDraft((d) => ({ ...d, service_id: e.target.value }))}>
                <option value="">Select a service…</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Payer (blank = hospital rate)">
              <NativeSelect value={draft.payer_id} onChange={(e) => setDraft((d) => ({ ...d, payer_id: e.target.value }))}>
                <option value="">Hospital</option>
                {payers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Room category">
              <Input value={draft.room_category} onChange={(e) => setDraft((d) => ({ ...d, room_category: e.target.value }))} />
            </FormField>
            <FormField label="Rate ₹" required>
              <Input type="number" min={0} value={draft.rate} onChange={(e) => setDraft((d) => ({ ...d, rate: e.target.value }))} />
            </FormField>
            <FormField label="Effective from">
              <Input type="date" value={draft.effective_from} onChange={(e) => setDraft((d) => ({ ...d, effective_from: e.target.value }))} />
            </FormField>
            <FormField label="Effective to">
              <Input type="date" value={draft.effective_to} onChange={(e) => setDraft((d) => ({ ...d, effective_to: e.target.value }))} />
            </FormField>
          </div>
          <div className="flex gap-2">
            <Button size="sm" disabled={saving || !draft.service_id || !draft.rate} loading={saving} onClick={submit}>Save tariff</Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setDraft(BLANK); }}>Cancel</Button>
          </div>
        </Card>
      )}
      <DataTable columns={columns} rows={items} rowKey={(t) => t.id} emptyLabel="No tariff rows yet" emptyHint="Add one above." />
    </div>
  );
}

type SubTab = 'packages' | 'tariffs';

export default function PayerTariffsPackagesTab() {
  const [settings, setSettings] = useState<PayerTariffsSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sub, setSub] = useState<SubTab>('packages');

  const load = useCallback(() => {
    getPayerTariffsSettings().then(setSettings).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleFlag = async () => {
    if (!settings) return;
    setSaving(true); setError('');
    try {
      const updated = await updatePayerTariffsSettings({ claims_payer_tariffs_enabled: !settings.claims_payer_tariffs_enabled });
      setSettings(updated);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setSaving(false);
  };

  if (!settings) return <LoadingState label="Loading settings…" />;

  return (
    <div className="fade-in">
      <Card className="mb-4 flex flex-wrap items-center justify-between gap-3 p-4">
        <div>
          <div className="text-sm font-medium">Payer tariffs & packages {settings.claims_payer_tariffs_enabled ? <Badge variant="success">On</Badge> : <Badge variant="neutral">Off</Badge>}</div>
          <div className="mt-1 text-xs text-ink-muted">
            Off: every new charge prices at the hospital&apos;s own service rate only, and a package cannot be applied to
            an admission. On: a payer-specific or hospital tariff row (below) can override a service&apos;s rate, and
            packages can be applied to admissions.
          </div>
        </div>
        <Button size="sm" variant={settings.claims_payer_tariffs_enabled ? 'ghost' : 'primary'} loading={saving} onClick={toggleFlag}>
          Turn {settings.claims_payer_tariffs_enabled ? 'off' : 'on'}
        </Button>
      </Card>

      {error && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

      <TabBar
        tabs={[{ id: 'packages' as const, label: '📦 Packages' }, { id: 'tariffs' as const, label: '💰 Payer Tariffs' }]}
        active={sub} onChange={setSub} size="sm" className="mb-4"
      />
      {sub === 'packages' ? <PackagesPanel /> : <PayerTariffsPanel />}
    </div>
  );
}
